import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SponsorshipOpportunities = () => {
  const [sponsorshipData, setSponsorshipData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Backend API Configuration
  const API_BASE_URL = "https://backend.dlapakistan.com/api/dla-trade/sponsorship-opportunity";

  // Axios instance
  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000,
  });

  // Fetch data from backend
  const fetchSponsorshipData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(API_BASE_URL + "/");
      
      if (response.data && Array.isArray(response.data)) {
        setSponsorshipData(response.data);
      } else {
        setSponsorshipData([]);
        setError("No sponsorship opportunities found.");
      }
    } catch (error) {
      console.error("Error fetching sponsorship data:", error);
      setError("Failed to load sponsorship opportunities. Please check your connection and try again.");
      setSponsorshipData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchSponsorshipData();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 font-sans text-[#333333]">

        {/* Main Title */}
        <h1 className="text-[42px] font-normal mb-4 text-[#3c4858]">Sponsorship Opportunities</h1>
        
        {/* Visual Separation Line */}
        <hr className="border-t border-gray-200 mb-10" />

        {/* Loading State */}
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#70B444] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sponsorship opportunities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6 font-sans text-[#333333]">
        {/* Breadcrumb Section */}
        <nav className="text-[13px] mb-4 flex items-center gap-1">
          <span className="text-[#70B444] hover:underline cursor-pointer">Home</span>
          <span className="text-gray-400">»</span>
          <span className="text-[#70B444] hover:underline cursor-pointer">DLA Trade</span>
          <span className="text-gray-400">»</span>
          <span className="text-gray-600">Sponsorship Opportunities</span>
        </nav>

        {/* Main Title */}
        <h1 className="text-[42px] font-normal mb-4 text-[#3c4858]">Sponsorship Opportunities</h1>
        
        {/* Visual Separation Line */}
        <hr className="border-t border-gray-200 mb-10" />

        {/* Error State */}
        <div className="text-center py-20 bg-red-50 rounded-lg">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button 
            onClick={fetchSponsorshipData}
            className="px-6 py-2 bg-[#70B444] text-white rounded-lg hover:bg-[#5a9c36] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (sponsorshipData.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-6 font-sans text-[#333333]">
        {/* Breadcrumb Section */}
        <nav className="text-[13px] mb-4 flex items-center gap-1">
          <span className="text-[#70B444] hover:underline cursor-pointer">Home</span>
          <span className="text-gray-400">»</span>
          <span className="text-[#70B444] hover:underline cursor-pointer">DLA Trade</span>
          <span className="text-gray-400">»</span>
          <span className="text-gray-600">Sponsorship Opportunities</span>
        </nav>

        {/* Main Title */}
        <h1 className="text-[42px] font-normal mb-4 text-[#3c4858]">Sponsorship Opportunities</h1>
        
        {/* Visual Separation Line */}
        <hr className="border-t border-gray-200 mb-10" />

        {/* Empty State */}
        <div className="text-center py-20 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg">No sponsorship opportunities available at the moment.</p>
          <p className="text-gray-500 mt-2">Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 font-sans text-[#333333]">
      {/* Breadcrumb Section */}
      <nav className="text-[13px] mb-4 flex items-center gap-1">
        <span className="text-[#70B444] hover:underline cursor-pointer">Home</span>
        <span className="text-gray-400">»</span>
        <span className="text-[#70B444] hover:underline cursor-pointer">DLA Trade</span>
        <span className="text-gray-400">»</span>
        <span className="text-gray-600">Sponsorship Opportunities</span>
      </nav>

      {/* Main Title */}
      <h1 className="text-[42px] font-normal mb-4 text-[#3c4858]">Sponsorship Opportunities</h1>
      
      {/* Visual Separation Line */}
      <hr className="border-t border-gray-200 mb-10" />

      {/* Render all sponsorship opportunities from backend */}
      {sponsorshipData.map((opportunity, index) => (
        <div 
          key={opportunity.id} 
          className={`${index < sponsorshipData.length - 1 ? 'mb-20' : ''} ${index > 0 ? 'pt-10 border-t border-gray-100' : ''}`}
        >
          {/* IMAGE ON TOP */}
          {opportunity.image_url && (
            <div className="mb-8">
              <img 
                src={opportunity.image_url}
                alt={opportunity.title || "Sponsorship Opportunity"} 
                className="w-full max-w-[300px] h-auto"
                onError={(e) => {
                  e.target.src = "/placeholder-image.jpg";
                  e.target.alt = "Image not available";
                }}
              />
            </div>
          )}
          
          {/* TITLE - Green color */}
          <h2 className="text-[28px] text-[#70B444] font-normal mb-4">
            {opportunity.title || "Untitled Opportunity"}
          </h2>
          
          {/* DESCRIPTION - HTML content from Jodit editor */}
          <div 
            className="space-y-6 text-[17px] leading-[1.6] text-[#555555] description-content"
            dangerouslySetInnerHTML={{ __html: opportunity.description || "" }}
          />
        </div>
      ))}
      
      {/* Add custom styles for the description content */}
      <style jsx>{`
        .description-content p {
          margin-bottom: 1.5rem;
        }
        
        .description-content ul {
          list-style-type: disc;
          padding-left: 2rem;
          margin-bottom: 1.5rem;
        }
        
        .description-content li {
          margin-bottom: 0.5rem;
        }
        
        .description-content strong, 
        .description-content b {
          font-weight: bold;
          color: #333333;
        }
        
        .description-content a {
          color: #70B444;
          text-decoration: underline;
          cursor: pointer;
        }
        
        .description-content a:hover {
          color: #5a9c36;
        }
      `}</style>
    </div>
  );
};

export default SponsorshipOpportunities;